/* ==========================================================================
   Personalization fields for made-to-order products.

   Self-contained on purpose: it adds a feature without editing theme.js, so
   the core file stays exactly as published and this can be removed by deleting
   one script tag.

   Three jobs:
     1. Re-reveal markup injected after page load (see the safety net below).
     2. Drive the personalization panel — how many name boxes, how many
        characters, file preview, character counters.
     3. Block add-to-cart while a required detail is missing.
   ========================================================================== */
(() => {
  'use strict';

  const FZ = (window.FZ = window.FZ || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Safety net: reveal markup injected after boot ----------
     Reveal-on-scroll holds every [data-reveal] element at opacity 0 until an
     IntersectionObserver marks it. That observer only ever sees the elements
     present when the page booted, so anything fetched afterwards — the
     "You may also love" carousel, a filtered collection grid, a lazily filled
     tab, the cart upsell — renders at full size and completely invisible.
     That is exactly how the recommendations carousel came to show its heading
     above a tall blank gap.

     Rather than patch each fetch site (and miss the next one), watch for
     injected nodes and re-run the theme's own observer over them. */
  const watchInjected = () => {
    if (!('MutationObserver' in window) || typeof FZ.observeReveal !== 'function') return;
    let queued = false;
    // setTimeout rather than requestAnimationFrame: a background tab never runs
    // animation frames, so rAF here would leave the batch flag stuck on and the
    // sweep would never happen — the exact failure this net exists to prevent,
    // and background tabs are ordinary (anyone middle-clicking a product link).
    const sweep = () => { queued = false; FZ.observeReveal(); };
    new MutationObserver((records) => {
      if (queued) return;
      for (const rec of records) {
        for (const node of rec.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches('[data-reveal]:not(.is-in)') || node.querySelector('[data-reveal]:not(.is-in)')) {
            queued = true;
            setTimeout(sweep, 0);
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    // Chrome delivers no IntersectionObserver notifications while a page is
    // hidden, so anything injected into a background tab is observed but never
    // revealed. Sweep again when the tab comes to the front.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) FZ.observeReveal();
    });

    // Anything injected before this script ran.
    FZ.observeReveal();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchInjected);
  else watchInjected();

  /* ---------- The panel ---------- */
  const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // Shopify's ceiling for an uploaded line item property

  class PersonalizationFields extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.error = $('[data-perso-error]', this);

      this.onVariant = (e) => {
        if (e.detail && e.detail.sectionId && e.detail.sectionId !== this.sectionId) return;
        this.sync((e.detail && e.detail.variant && e.detail.variant.options) || null);
      };
      document.addEventListener('variant:change', this.onVariant);

      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-perso-input]')) { this.count(e.target); this.clearError(); }
      });
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-perso-file]')) this.onFile(e.target);
        else if (e.target.matches('[data-perso-input]')) this.clearError();
      });

      this.sync(null);
      $$('[data-perso-input]', this).forEach((el) => this.count(el));
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariant);
    }

    /* "3 names" -> 3, "With 5 letters" -> 5, "10" -> 10, "Single Letter" -> 1 */
    static numberIn(value) {
      if (!value) return 0;
      const m = String(value).match(/\d+/);
      if (m) return parseInt(m[0], 10);
      return /single|one\b/i.test(value) ? 1 : 0;
    }

    optionValue(idx, options) {
      if (idx === '' || idx == null || Number.isNaN(Number(idx))) return '';
      if (options && options[Number(idx)] != null) return options[Number(idx)];
      return '';
    }

    sync(options) {
      // How many name boxes to show
      $$('[data-perso-repeat]', this).forEach((group) => {
        const fixed = parseInt(group.dataset.fixed || '0', 10);
        const max = parseInt(group.dataset.max || '12', 10);
        let count = fixed;
        if (!count) {
          const live = this.optionValue(group.dataset.countOption, options);
          const value = live || group.dataset.countValue || '';
          if (live) group.dataset.countValue = live;
          count = PersonalizationFields.numberIn(value) || 1;
        }
        count = Math.max(1, Math.min(count, max));
        $$('.perso__field', group).forEach((field, i) => {
          const on = i < count;
          field.hidden = !on;
          const input = $('[data-perso-input]', field);
          if (input) {
            input.disabled = !on; // a disabled input is not submitted, so no empty properties
            if (!on) input.value = '';
          }
        });
      });

      // Character limit that follows a variant option ("With 5 letters")
      $$('[data-perso-maxlen-option]', this).forEach((field) => {
        const live = this.optionValue(field.dataset.persoMaxlenOption, options);
        const value = live || field.dataset.countValue || '';
        if (live) field.dataset.countValue = live;
        const n = PersonalizationFields.numberIn(value);
        const input = $('[data-perso-input]', field);
        if (!input || !n) return;
        input.maxLength = n;
        if (input.value.length > n) input.value = input.value.slice(0, n);
        this.count(input);
      });
    }

    count(input) {
      const field = input.closest('.perso__field');
      const counter = field && $('[data-perso-counter]', field);
      if (!counter || !input.maxLength || input.maxLength < 0) return;
      counter.textContent = `${input.value.length}/${input.maxLength}`;
    }

    onFile(input) {
      const field = input.closest('.perso__field');
      const preview = field && $('[data-perso-preview]', field);
      const file = input.files && input.files[0];
      this.clearError();
      if (!file) { if (preview) { preview.hidden = true; preview.innerHTML = ''; } return; }
      if (file.size > MAX_UPLOAD_BYTES) {
        input.value = '';
        if (preview) { preview.hidden = true; preview.innerHTML = ''; }
        this.showError(FZ.strings && FZ.strings.personalizeTooLarge);
        return;
      }
      if (!preview) return;
      preview.innerHTML = '';
      if (/^image\//.test(file.type)) {
        const img = document.createElement('img');
        img.alt = '';
        img.src = URL.createObjectURL(file);
        img.onload = () => URL.revokeObjectURL(img.src);
        preview.appendChild(img);
      }
      const name = document.createElement('span');
      name.textContent = file.name;
      preview.appendChild(name);
      preview.hidden = false;
    }

    showError(msg) {
      if (!this.error) return;
      this.error.textContent = msg || 'Please complete the details above.';
      this.error.hidden = false;
    }

    clearError() {
      if (this.error) this.error.hidden = true;
      $$('.perso__field.has-error', this).forEach((f) => f.classList.remove('has-error'));
    }

    /* True when every required, currently visible field has been filled in. */
    validate() {
      this.clearError();
      let firstBad = null;
      $$('[data-perso-required]', this).forEach((input) => {
        const field = input.closest('.perso__field');
        if (input.disabled || (field && field.hidden)) return;
        const empty = input.type === 'file' ? !(input.files && input.files.length) : !input.value.trim();
        if (empty) {
          if (field) field.classList.add('has-error');
          if (!firstBad) firstBad = input;
        }
      });
      if (!firstBad) return true;
      this.showError(FZ.strings && FZ.strings.personalizeRequired);
      const field = firstBad.closest('.perso__field');
      if (field) field.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      try { firstBad.focus({ preventScroll: true }); } catch (e) { firstBad.focus(); }
      return false;
    }
  }
  customElements.define('personalization-fields', PersonalizationFields);

  /* ---------- Guard add-to-cart ----------
     The theme's <product-form> listens for submit on the form itself. This
     listens on the document during the capture phase, so it runs first and can
     stop the event before the cart ever sees it. Keeps the check here instead
     of inside theme.js. */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.matches || !form.matches('form')) return;
    const perso = form.querySelector('personalization-fields');
    if (!perso || typeof perso.validate !== 'function') return;
    if (!perso.validate()) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // An optional field left blank would otherwise ride along as an empty
    // property and show up as a bare label on the order. A disabled input is
    // not serialized, so switch them off just long enough for the form to be
    // read, then restore them so the shopper can still type.
    const blanks = $$('[data-perso-input]', perso).filter((input) => {
      if (input.disabled || input.hasAttribute('data-perso-required')) return false;
      return input.type === 'file' ? !(input.files && input.files.length) : !input.value.trim();
    });
    blanks.forEach((input) => { input.disabled = true; });
    setTimeout(() => blanks.forEach((input) => { input.disabled = false; }), 0);
  }, true);
})();
